/*
 * Note: This is non-end-user facing code so no need for i18n strings.
 */
import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { clearConversationLoggingRows } from "@mirage/service-conversation";
import { useConversationLoggingRows } from "@mirage/service-conversation/hooks/useConversationDevLogging";
import { parseRow } from "@mirage/service-conversation/service/conversationsLogger/conversationResponseLogger";
import type { ConversationLoggingRow } from "@mirage/service-conversation/service/conversationsLogger/types";
import { tagged } from "@mirage/service-logging";
import { getTimeAgoString } from "@mirage/shared/util/time";
import { memo } from "react";

const logger = tagged("ConversationLoggingRows");

export const ConversationLoggingRows = memo(() => {
  const { rows, refresh } = useConversationLoggingRows();
  return (
    <div>
      <Box display="block" paddingBottom="16">
        <Text size="medium" isBold>
          Conversation Logging
        </Text>
        &nbsp;&nbsp;
        <Text size="medium" color="subtle">
          {rows.length} rows
        </Text>
      </Box>
      <Box display="block" paddingBottom="8">
        <Button
          variant="opacity"
          onClick={() => {
            downloadConversationLoggingRows(rows);
          }}
        >
          Download
        </Button>
        &nbsp;&nbsp;
        <Button
          variant="opacity"
          tone="destructive"
          onClick={async () => {
            await clearConversationLoggingRows();
            refresh();
          }}
        >
          Clear
        </Button>
      </Box>
      <div>
        {rows.map((row, i) => (
          <ConversationLoggingRowItem key={i} row={row} index={i} />
        ))}
      </div>
    </div>
  );
});
ConversationLoggingRows.displayName = "ConversationLoggingRows";

function downloadConversationLoggingRows(rows: string[]) {
  // rows are already JSON strings, so we just want to wrap them in an array and save as a JSON file
  const lines = "[\n" + rows.join(",\n\n") + "\n]";
  const blob = new Blob([lines], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "conversation-logs.json";
  a.click();
}

interface ConversationLoggingRowItemProps {
  row: string;
  index: number;
}
export const ConversationLoggingRowItem = memo(
  ({ row, index }: ConversationLoggingRowItemProps) => {
    const parsed = parseConversationLoggingRow(row);
    if (!parsed) {
      return (
        <div>
          <Text color="error">Error parsing row</Text>
        </div>
      );
    }
    const lastMessage =
      parsed.response.messages[parsed.response.messages.length - 1];
    return (
      <div>
        <Text color="subtle">{index + 1}</Text>
        &nbsp;&nbsp;
        <Text>{parsed.request.userMessage?.text}</Text>
        &nbsp;&nbsp;
        <Text color="subtle">
          {getTimeAgoString(
            new Date(
              Number(lastMessage.timestamp?.seconds || 0) * 1000,
            ).valueOf(),
          )}
        </Text>
      </div>
    );
  },
);
ConversationLoggingRowItem.displayName = "ConversationLoggingRowItem";

function parseConversationLoggingRow(
  row: string,
): ConversationLoggingRow | undefined {
  try {
    return parseRow(row) as ConversationLoggingRow;
  } catch (e) {
    logger.error(e);
    return undefined;
  }
}
